import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { safeUUID } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Save, FileCheck, Loader2, AlertCircle, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { generatePdfPreview } from "@/lib/pdfUtils";
import { generateDocumentPdf } from "@/lib/pdfGeneration";
import { FinalizeWithSignatures } from "@/components/documents/FinalizeWithSignatures";
import { sanitizeDocumentContent } from "@/lib/sanitize";
import { generatedFilePath } from '@/components/workflows/docProvenance';

// Helper to convert readable date to ISO format (for date inputs)
const toISODate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  } catch {
    return '';
  }
};

// Helper to convert ISO date to readable format (for display/storage)
const toReadableDate = (isoStr: string): string => {
  if (!isoStr) return '';
  try {
    const date = new Date(isoStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch {
    return isoStr;
  }
};

/**
 * Convert base64 string to Blob for file upload
 */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

/**
 * Fetch client IP address for audit trail
 */
async function getClientIP(): Promise<string> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('⚠️ Could not fetch IP address:', error);
    return 'unknown';
  }
}

// Normalize member-related keys to support both member_* and new_member_* prefixes
const normalizeMemberKeys = (data: Record<string, any>): Record<string, any> => {
  const d = { ...(data || {}) };

  const ensure = (from: string, to: string) => {
    if (d[from] && !d[to]) d[to] = d[from];
  };

  // Names
  ensure('member_first_name', 'new_member_first_name');
  ensure('member_last_name', 'new_member_last_name');
  ensure('member_full_name', 'new_member_full_name');
  ensure('new_member_first_name', 'member_first_name');
  ensure('new_member_last_name', 'member_last_name');
  ensure('new_member_full_name', 'member_full_name');

  // Address
  ensure('member_address', 'new_member_address');
  ensure('new_member_address', 'member_address');

  // Contact
  ensure('member_email', 'new_member_email');
  ensure('member_phone', 'new_member_phone');
  ensure('new_member_email', 'member_email');
  ensure('new_member_phone', 'member_phone');

  // Role
  ensure('member_role', 'new_member_role');
  ensure('new_member_role', 'member_role');

  // Dates
  ensure('appointment_date', 'new_member_appointment_date');
  ensure('new_member_appointment_date', 'appointment_date');

  // City/State/Zip
  ensure('member_city', 'city');
  ensure('city', 'member_city');
  ensure('member_state', 'state');
  ensure('state', 'member_state');
  ensure('member_zip', 'zip_code');
  ensure('zip_code', 'member_zip');

  // Compose full name if missing
  if (!d.member_full_name && d.member_first_name && d.member_last_name) {
    d.member_full_name = `${d.member_first_name} ${d.member_last_name}`.trim();
  }
  if (!d.new_member_full_name && d.new_member_first_name && d.new_member_last_name) {
    d.new_member_full_name = `${d.new_member_first_name} ${d.new_member_last_name}`.trim();
  }

  return d;
};

/**
 * Extract body content from full HTML document.
 * Handles custom documents that have <html><head><body> structure.
 */
const extractBodyContent = (html: string): string => {
  if (!html) return '';
  
  // Check if this is a full HTML document
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch && bodyMatch[1]) {
    return bodyMatch[1].trim();
  }
  
  // If no body tags, return as-is (it's already a fragment)
  return html.trim();
};

export default function DocumentEditor() {
  console.log('🟨🟨🟨 DOCUMENT EDITOR PAGE RENDERED 🟨🟨🟨');
  
  const { taskId, documentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [document, setDocument] = useState<any>(null);
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mergeFields, setMergeFields] = useState<any[]>([]);
  const [mergeValues, setMergeValues] = useState<Record<string, any>>({});
  const [activeSection, setActiveSection] = useState<number>(0);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [isWorkflowDocument, setIsWorkflowDocument] = useState(false);
  const [addMemberContext, setAddMemberContext] = useState(false); // ✨ PHASE 2: Track add member workflow context
  
  // Preview state
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [hasCustomEdits, setHasCustomEdits] = useState<boolean>(false);
  
  // Signature modal state (for Meeting Minutes)
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);

  // Handle back navigation
  const handleBack = () => {
    const destination = isWorkflowDocument ? '/workflows?tab=my-workflows' : '/governance/tasks';
    const title = isWorkflowDocument ? "Returning to Workflows" : "Returning to Tasks";
    const description = isWorkflowDocument 
      ? "You can continue editing later from your workflows" 
      : "You can continue editing later from your tasks";
    
    toast({
      title,
      description,
    });
    navigate(destination);
  };

  useEffect(() => {
    loadDocument();
  }, [documentId]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  const selfHealSections = async (docData: any, docId: string) => {
    try {
      console.log('Self-healing: Creating missing sections');
      
      // Get the template (library or legacy)
      const templateId = docData.template_library_id || docData.template_id;
      if (!templateId) {
        toast({
          title: "Cannot create sections",
          description: "This document has no source to build from. Please contact support.",
          variant: "destructive",
        });
        setSections([]);
        return;
      }
      
      // Try library first
      let template: any = null;
      const { data: libTemplate } = await supabase
        .from('document_template_library')
        .select('*')
        .eq('id', templateId)
        .order('version', { ascending: false })
        .limit(1)
        .single();
      
      if (libTemplate) {
        template = libTemplate;
      } else {
        // Try legacy
        const { data: legacyTemplate } = await supabase
          .from('document_templates')
          .select('*')
          .eq('id', templateId)
          .single();
        
        template = legacyTemplate;
      }
      
      if (!template) {
        toast({
          title: "Source not found",
          description: "We could not find the source for this document. Please contact support.",
          variant: "destructive",
        });
        setSections([]);
        return;
      }
      
      // Create sections from template
      const mergeData = docData.merge_data || {};
      const sectionsToCreate: any[] = [];
      
      if (template.sections && Array.isArray(template.sections) && template.sections.length > 0) {
        // Template has sections
        template.sections.forEach((section: any, index: number) => {
          sectionsToCreate.push({
            document_id: docId,
            section_number: index + 1,
            section_title: section.title || section.section_title || `Section ${index + 1}`,
            section_content: section.content_template || section.content || '<p>Empty section</p>',
            is_editable: section.is_editable !== false,
            is_locked: section.is_locked === true,
          });
        });
      } else if (template.template_content) {
        // No sections, create one from template_content
        sectionsToCreate.push({
          document_id: docId,
          section_number: 1,
          section_title: template.template_name || 'Document Content',
          section_content: template.template_content || '<p>Empty document</p>',
          is_editable: true,
          is_locked: false,
        });
      }
      
      if (sectionsToCreate.length > 0) {
        const { data: newSections, error } = await supabase
          .from('document_sections')
          .insert(sectionsToCreate)
          .select();
        
        if (error) throw error;
        
        setSections(newSections || []);
        toast({
          title: "Sections Created",
          description: `Created ${newSections?.length || 0} section(s) from template`,
        });
      } else {
        toast({
          title: "No content available",
          description: "This document has no sections or content yet.",
          variant: "destructive",
        });
        setSections([]);
      }
    } catch (error: any) {
      console.error('Self-heal error:', error);
      toast({
        title: "Failed to Create Sections",
        description: error.message,
        variant: "destructive",
      });
      setSections([]);
    }
  };

  // Post-process HTML to remove optional heading, hide empty optional sections, and strip inline fonts
  const postProcessHtml = (html: string, optionalContent?: string): string => {
    let processed = html;
    
    // Remove any "Optional Additional Content" heading (any h1-h6 tag)
    processed = processed.replace(
      /<h[1-6][^>]*>\s*Optional\s+Additional\s+Content\s*<\/h[1-6]>/gi,
      ''
    );
    
    // If optional content is empty, remove the entire optional-content container
    if (!optionalContent || !optionalContent.trim()) {
      processed = processed.replace(
        /<div[^>]*class="[^"]*optional-content[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
        ''
      );
    }
    
    // Strip inline font-family styles to allow Cambria to take effect
    processed = processed.replace(/style="([^"]*)font-family:[^;"]+(;?)([^"]*)"/gi, (match, before, semi, after) => {
      const cleaned = (before + after).replace(/;\s*;/g, ';').trim();
      return cleaned ? `style="${cleaned}"` : '';
    });
    
    // Replace <pre> tags with <div style="white-space: pre-line;"> for board member lists
    processed = processed.replace(/<pre([^>]*)>/gi, '<div$1 style="white-space: pre-line;">');
    processed = processed.replace(/<\/pre>/gi, '</div>');
    
    return processed;
  };

  // Handle merge field changes with diagnostic logging
  const handleMergeFieldChange = useCallback((fieldName: string, newValue: any) => {
    console.log('🔧 MERGE FIELD CHANGE DETECTED:');
    console.log('  - Field name:', fieldName);
    console.log('  - Old value:', mergeValues[fieldName]);
    console.log('  - New value:', newValue);
    console.log('  - Is array?', Array.isArray(newValue));
    
    setMergeValues(prev => {
      const updated = { ...prev, [fieldName]: newValue };
      
      console.log('  - Updated mergeValues:', updated);
      console.log('  - whereas_clauses after update:', updated.whereas_clauses);
      console.log('  - resolved_clauses after update:', updated.resolved_clauses);
      
      return updated;
    });
  }, [mergeValues]);

  const updatePreview = useCallback(async () => {
    if (!document) return;

    try {
      // Priority 1: Use custom edits if they exist
      if (document.custom_rendered_content) {
        const processed = postProcessHtml(document.custom_rendered_content, mergeValues.optional_added_section);
        setPreviewHtml(processed);
        setHasCustomEdits(true);
        return;
      }

      // Priority 2: Custom document content
      if (document.is_custom) {
        const bodyContent = extractBodyContent(document.custom_content || '');
        setPreviewHtml(bodyContent || '<p class="text-muted-foreground">Empty document</p>');
        return;
      }

      // Priority 3: Render template with current merge values
      const templateId = document.template_library_id || document.template_id;
      const templateTable = document.template_library_id 
        ? 'document_template_library' 
        : 'document_templates';
      
      const { data: template } = await supabase
        .from(templateTable)
        .select('template_content')
        .eq('id', templateId)
        .order('version', { ascending: false })
        .limit(1)
        .single();
      
      if (template?.template_content) {
        const Handlebars = (await import('handlebars')).default;
        
        // Register Handlebars helpers for conditional logic in templates
        Handlebars.registerHelper('eq', function(a: any, b: any) {
          return a === b;
        });

        Handlebars.registerHelper('or', function(a: any, b: any) {
          return a || b;
        });

        Handlebars.registerHelper('and', function(a: any, b: any) {
          return a && b;
        });
        
        console.log('📄 TEMPLATE HTML CHECK:');
        console.log('  - Template contains {{#each whereas_clauses}}?', 
          template.template_content.includes('{{#each whereas_clauses}}') ||
          template.template_content.includes('{{whereas_clauses}}')
        );
        console.log('  - Template contains {{#each resolved_clauses}}?',
          template.template_content.includes('{{#each resolved_clauses}}') ||
          template.template_content.includes('{{resolved_clauses}}')
        );
        console.log('  - Template HTML (first 2000 chars):', template.template_content.substring(0, 2000));
        
        // ✨ STEP 6 DIAGNOSTIC: Log merge values being used for template compilation
        console.log('🔍 MERGE VALUES BEING USED FOR TEMPLATE:', {
          allMergeValues: mergeValues,
          member_full_name: mergeValues.member_full_name,
          new_member_full_name: mergeValues.new_member_full_name,
          member_first_name: mergeValues.member_first_name,
          new_member_first_name: mergeValues.new_member_first_name,
          member_address: mergeValues.member_address,
          new_member_address: mergeValues.new_member_address,
        });
        
        console.log('🎨 COMPILING TEMPLATE WITH DATA:');
        console.log('  - whereas_clauses:', mergeValues.whereas_clauses);
        console.log('  - whereas_clauses type:', typeof mergeValues.whereas_clauses);
        console.log('  - whereas_clauses is array?', Array.isArray(mergeValues.whereas_clauses));
        console.log('  - whereas_clauses length:', mergeValues.whereas_clauses?.length);
        console.log('  - whereas_clauses value:', JSON.stringify(mergeValues.whereas_clauses));
        console.log('  - resolved_clauses:', mergeValues.resolved_clauses);
        console.log('  - resolved_clauses type:', typeof mergeValues.resolved_clauses);
        console.log('  - resolved_clauses is array?', Array.isArray(mergeValues.resolved_clauses));
        console.log('  - resolved_clauses length:', mergeValues.resolved_clauses?.length);
        console.log('  - resolved_clauses value:', JSON.stringify(mergeValues.resolved_clauses));
        
        // CRITICAL FIX: Ensure array fields are actually arrays before compilation
        const preparedMergeData = { ...mergeValues };
        
        // Convert whereas_clauses to array if it's a string
        if (typeof preparedMergeData.whereas_clauses === 'string') {
          preparedMergeData.whereas_clauses = preparedMergeData.whereas_clauses
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line.length > 0);
          
          console.log('✅ Converted whereas_clauses string to array:', preparedMergeData.whereas_clauses);
        }
        
        // Convert resolved_clauses to array if it's a string
        if (typeof preparedMergeData.resolved_clauses === 'string') {
          preparedMergeData.resolved_clauses = preparedMergeData.resolved_clauses
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line.length > 0);
          
          console.log('✅ Converted resolved_clauses string to array:', preparedMergeData.resolved_clauses);
        }
        
        // IMPORTANT: Ensure board_members stays as array of objects (DON'T convert to string!)
        if (Array.isArray(preparedMergeData.board_members)) {
          console.log('✅ board_members is array:', preparedMergeData.board_members.length, 'members');
          console.log('   First member:', preparedMergeData.board_members[0]);
        } else {
          console.warn('⚠️ board_members is not an array:', typeof preparedMergeData.board_members);
          // If it's missing or invalid, set to empty array to prevent template errors
          preparedMergeData.board_members = [];
        }

        // Ensure requires_board_approval is boolean
        if (typeof preparedMergeData.requires_board_approval === 'undefined') {
          // Default to true if board_members exist
          preparedMergeData.requires_board_approval = preparedMergeData.board_members.length > 0;
          console.log('✅ Auto-set requires_board_approval:', preparedMergeData.requires_board_approval);
        }
        
        console.log('📊 FINAL DATA FOR COMPILATION:');
        console.log('  - whereas_clauses is now array?', Array.isArray(preparedMergeData.whereas_clauses));
        console.log('  - whereas_clauses length:', preparedMergeData.whereas_clauses?.length);
        console.log('  - resolved_clauses is now array?', Array.isArray(preparedMergeData.resolved_clauses));
        console.log('  - resolved_clauses length:', preparedMergeData.resolved_clauses?.length);
        
        const compiled = Handlebars.compile(template.template_content);
        let html = compiled(preparedMergeData);
        
        console.log('  - Compiled HTML contains "WHEREAS"?', html.includes('WHEREAS'));
        console.log('  - Compiled HTML contains "RESOLVED"?', html.includes('RESOLVED'));
        console.log('  - First 1000 chars of compiled HTML:', html.substring(0, 1000));
        
        // Post-process to remove heading and handle empty optional content
        html = postProcessHtml(html, mergeValues.optional_added_section);

        setPreviewHtml(html);
      } else {
        // Priority 4: Fallback to sections
        const html = sections
          .map(s => `<h2>${s.section_title || ''}</h2>${s.section_content || ''}`)
          .join('<hr class="my-4"/>');
        setPreviewHtml(html || '<p class="text-muted-foreground">No content available</p>');
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      toast({ 
        title: "Preview Error", 
        description: error.message,
        variant: "destructive" 
      });
      setPreviewHtml('<p class="text-destructive">Error loading preview</p>');
    }
  }, [document, sections, mergeValues]);

  // Expose mergeValues to window for debugging
  useEffect(() => {
    (window as any).__mergeValues = mergeValues;
    console.log('🪟 Exposed mergeValues to window.__mergeValues for debugging');
  }, [mergeValues]);

  const loadDocument = async () => {
    // Validate UUIDs before querying
    if (!documentId || !safeUUID(documentId)) {
      console.error(`Invalid document ID: "${documentId}"`);
      toast({
        title: "Invalid Document",
        description: "The document ID is invalid.",
        variant: "destructive",
      });
      navigate('/governance/tasks');
      return;
    }

    if (taskId && !safeUUID(taskId)) {
      console.error(`Invalid task ID: "${taskId}"`);
      toast({
        title: "Invalid Task",
        description: "The task ID is invalid.",
        variant: "destructive",
      });
      navigate('/governance/tasks');
      return;
    }

    console.log('🔍🔍🔍 FETCH MERGE FIELD DATA STARTED 🔍🔍🔍');
    console.log('Document ID:', documentId);
    console.log('Task ID:', taskId);

    try {
      const { data: docData, error: docError } = await supabase
        .from('generated_documents')
        .select(`
          *,
          template_lib:document_template_library!template_library_id(
            template_name,
            template_key,
            merge_field_definitions,
            signature_blocks
          ),
          template_legacy:document_templates!template_id(
            template_name,
            merge_fields
          )
        `)
        .eq('id', documentId)
        .single();

      if (docError) throw docError;
      
      // Set document initially (will be updated with workflow info if applicable)
      setDocument(docData);
      
      // Fetch workflow key for filtering
      let workflowKey: string | undefined;
      if (taskId) {
        const { data: taskData } = await supabase
          .from('custom_tasks')
          .select(`
            is_from_workflow,
            workflow_id,
            workflows!custom_tasks_workflow_id_fkey (
              workflow_key,
              title
            )
          `)
          .eq('id', taskId)
          .single();
        
        console.log('📦 Task data fetched for filtering:', {
          workflow_id: taskData?.workflow_id,
          workflows: taskData?.workflows,
          workflow_key: (taskData?.workflows as any)?.workflow_key
        });
        
        workflowKey = (taskData?.workflows as any)?.workflow_key;
        
        // Update document with workflow info for later detection
        if (workflowKey) {
          setDocument((prev: any) => ({
            ...prev,
            workflow: { workflow_key: workflowKey }
          }));
        }
        
        console.log('🔴 WORKFLOW KEY DETECTION:');
        console.log('  - taskData:', taskData);
        console.log('  - taskData.workflows:', taskData?.workflows);
        console.log('  - workflow_key extracted:', workflowKey);
        console.log('  - Type of workflow_key:', typeof workflowKey);
        
        // Fallback: if join failed but we have workflow_id, fetch it directly
        if (!workflowKey && taskData?.workflow_id) {
          console.warn('⚠️ Workflow join failed, fetching workflow_key directly');
          const { data: workflow } = await supabase
            .from('workflows')
            .select('workflow_key')
            .eq('id', taskData.workflow_id)
            .single();
          
          workflowKey = workflow?.workflow_key;
          console.log('✅ Fetched workflow_key via fallback:', workflowKey);
        }
        
        if (!workflowKey) {
          console.error('❌ Could not determine workflow_key for task:', taskId);
        }
        
        setIsWorkflowDocument(taskData?.is_from_workflow || false);
      }
      
      // Set custom edits flag if it exists
      if (docData.has_custom_edits) {
        setHasCustomEdits(true);
      }

      // Handle custom documents differently
      if (docData.is_custom) {
        // Custom documents don't have sections or merge fields from templates
        setMergeFields([]);
        setMergeValues({});
        setSections([]);
      } else {
        // Template-based documents
        // Parse merge_data to get existing values
        const rawMergeData = (typeof docData.merge_data === 'object' ? docData.merge_data : {}) as Record<string, any>;
        
        // ✅ PHASE 1: Normalize member keys for add_member workflows with logging
        let mergeData = rawMergeData;
        
        if (workflowKey === 'add_member_simple' || workflowKey === 'add_una_member') {
          console.log('🔧 Normalizing member keys for add_member workflow');
          console.log('Before normalization:', {
            member_full_name: rawMergeData.member_full_name,
            new_member_full_name: rawMergeData.new_member_full_name,
            member_address: rawMergeData.member_address,
            new_member_address: rawMergeData.new_member_address,
          });
          
          mergeData = normalizeMemberKeys(rawMergeData);
          
          console.log('✅ After normalization:', {
            member_full_name: mergeData.member_full_name,
            new_member_full_name: mergeData.new_member_full_name,
            member_address: mergeData.member_address,
            new_member_address: mergeData.new_member_address,
          });
        } else {
          // Apply normalization for all other documents (existing behavior)
          mergeData = normalizeMemberKeys(rawMergeData);
        }
        
        console.log('🔴 MERGE DATA SOURCE TRACE:');
        console.log('  - Document ID:', documentId);
        console.log('  - Task ID:', taskId);
        console.log('  - Raw merge_data:', rawMergeData);
        console.log('  - Has board_members array?', Array.isArray(mergeData.board_members));
        console.log('  - board_members value:', mergeData.board_members);
        console.log('  - Has members array?', Array.isArray(mergeData.members));
        console.log('  - members value:', mergeData.members);
        console.log('  - Has documents_filed?', mergeData.documents_filed);
        
        console.log('🎯 All merge data BEFORE filtering:', Object.keys(mergeData).length, 'fields');
        console.log('🔍 Editor: Normalized merge data keys:', Object.keys(mergeData));
        console.log('🔍 Editor: Member fields available:', {
          member_full_name: mergeData.member_full_name,
          new_member_full_name: mergeData.new_member_full_name,
          member_role: mergeData.member_role,
          new_member_role: mergeData.new_member_role,
        });
        
        // Try to get merge field definitions from either template format
        let templateMergeFields: any[] = [];
        
        // Check new template_library format first
        const templateMergeFieldsRaw = docData.template_lib?.merge_field_definitions;
        if (Array.isArray(templateMergeFieldsRaw) && templateMergeFieldsRaw.length > 0) {
          templateMergeFields = templateMergeFieldsRaw;
        }
        // Check legacy document_templates format
        else if (docData.template_legacy?.merge_fields) {
          // Legacy format: merge_fields is an object like:
          // { field_name: { label, type, required, help_text }, ... }
          const legacyFields = docData.template_legacy.merge_fields;
          templateMergeFields = Object.keys(legacyFields).map(key => ({
            name: key,
            label: legacyFields[key].label || key,
            type: legacyFields[key].type || 'text',
            required: legacyFields[key].required || false,
            help_text: legacyFields[key].help_text || ''
          }));
        }
        
        // Build fields list from template definitions or existing merge_data keys
        const normalize = (f: any) => {
          // ✅ FIX: Database uses 'key' not 'name'
          const fieldName = f.key ?? f.name ?? f.field_name ?? '';
          let fieldType = f.type ?? f.field_type ?? 'text';
          
          // Auto-detect date fields by name if type not specified
          if (fieldType === 'text' && (
            fieldName.includes('_date') || 
            fieldName.includes('Date') ||
            fieldName === 'signature_date' ||
            fieldName === 'appointment_date' ||
            fieldName === 'effective_date' ||
            fieldName === 'una_constitution_date'
          )) {
            fieldType = 'date';
          }
          
          // ✅ FIX: Include label for proper display
          return {
            name: fieldName,
            label: f.label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
            type: fieldType,
            description: f.description ?? f.help_text ?? '',
            required: !!f.required,
          };
        };

        // ✅ Build fields: Use template definitions OR fallback to all merge data keys
        let fields = (templateMergeFields.length > 0
          ? templateMergeFields.map(normalize)
          : Object.keys(mergeData)
              .filter(key => mergeData[key]) // Only show fields with values
              .map(key => ({
                name: key,
                label: key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
                type: key.includes('_date') ? 'date' : 'text',
                description: '',
                required: false,
              }))
        );
        
        console.log('🎯 WORKFLOW KEY FOR FILTERING:', workflowKey);
        console.log('✅ Normalized fields BEFORE filtering:', fields.length, 'fields');
        
        console.log('🔴 FIELDS BEFORE FILTERING:');
        console.log('  - Count:', fields.length);
        console.log('  - Field names:', fields.map(f => f.name).sort());
        console.log('  - Includes board_members?', fields.some(f => f.name === 'board_members'));
        console.log('  - Includes members?', fields.some(f => f.name === 'members'));
        console.log('  - Includes documents_filed?', fields.some(f => f.name === 'documents_filed'));

        // Serialize arrays to strings BEFORE filtering (prevents [object Object])
        if (Array.isArray(mergeData.board_members)) {
          mergeData.board_members_list = mergeData.board_members
            .map((bm: any, idx: number) =>
              `${idx + 1}. ${bm.full_name || bm.name || 'N/A'} - ${bm.role || 'Member'}`
            )
            .join('\n');
          console.log('✅ Serialized board_members to board_members_list');
        }

        if (Array.isArray(mergeData.members)) {
          mergeData.members_list = mergeData.members
            .map((m: any) => m.full_name || m.name)
            .filter(Boolean)
            .join(', ');
          console.log('✅ Serialized members to members_list');
        }

        // Apply workflow-specific filtering for resolution and meeting minutes workflows
        const templateKey = docData.template_lib?.template_key || '';
        const isResolutionTemplate = templateKey.includes('resolution');
        const isMeetingMinutesTemplate = templateKey === 'formal_meeting_minutes';
        const resolutionContext = (workflowKey === 'writing_resolution') || isResolutionTemplate;
        const meetingMinutesContext = (workflowKey === 'recording_minutes' || workflowKey === 'conducting_a_meeting') || isMeetingMinutesTemplate;
        const isAddMemberWorkflow = (workflowKey === 'add_member_simple' || workflowKey === 'add_una_member'); // ✨ PHASE 2
        
        // Set component state for add member context (used in sidebar rendering)
        setAddMemberContext(isAddMemberWorkflow);
        
        // Initialize uiEditableFields - will be set for resolution/meeting context
        let uiEditableFields: any[] = [];
        
        console.log('🔴 WORKFLOW CONTEXT DETECTION:');
        console.log('  - workflowKey:', workflowKey);
        console.log('  - templateKey:', templateKey);
        console.log('  - resolutionContext:', resolutionContext);
        console.log('  - meetingMinutesContext:', meetingMinutesContext);
        console.log('  - addMemberContext:', isAddMemberWorkflow);

        if (resolutionContext) {
          const beforeCount = fields.length;
          
          // Define allowed fields for resolution workflow - 6 editable + 3 auto-populated
          const allowedResolutionFields = [
            // Core editable fields (6 fields users can edit)
            'una_name',
            'resolution_title',
            'resolution_category',
            'current_date',
            'whereas_clauses',
            'resolved_clauses',
            
            // Auto-populated signature fields (needed for template, but not shown in UI)
            'treasury_secretary_name',
            'board_members',              // Array of {full_name, role} objects
            'requires_board_approval',    // Boolean flag
          ];

          console.log('🔒 RESOLUTION FIELDS: 6 editable + 3 auto-populated for signatures');

          // Filter to only allowed fields
          fields = fields.filter((f: any) => allowedResolutionFields.includes(f.name));

          // NOW: Remove auto-populated fields from the editable UI panel
          // (They're in the data for the template, but users shouldn't see/edit them)
          uiEditableFields = fields.filter((f: any) => 
            f.name !== 'treasury_secretary_name' && 
            f.name !== 'board_members' &&
            f.name !== 'requires_board_approval'
          );

          console.log('✅ Editable fields for UI:', uiEditableFields.length); // Should be 6
          console.log('✅ Hidden auto-populated fields:', ['treasury_secretary_name', 'board_members', 'requires_board_approval']);


          console.log('📊 RESOLUTION FILTERING SUMMARY:');
          console.log('  - Before filtering:', beforeCount, 'fields');
          console.log('  - After filtering:', fields.length, 'fields');
          console.log('  - UI editable fields:', uiEditableFields.length);
        } else if (meetingMinutesContext) {
          const beforeCount = fields.length;
          
          console.log('📋 MEETING MINUTES: View-only mode (Phase 2)');
          console.log('  - All data entered in Phase 1');
          console.log('  - No merge fields editable in Phase 2');
          
          // Define ONLY 3 editable fields for Phase 2 (final certification)
          const editableFields = ['una_name', 'board_members_list', 'signature_date'];
          
          // All Phase 1 data fields that should be preserved in merge_data but hidden from UI
          const preservedPhase1Fields = [
            'meeting_type', 'meeting_date', 'meeting_start_time', 'meeting_end_time', 'meeting_location',
            'meeting_purpose', 'president_name', 'recording_secretary_name',
            'actual_attendees', 'expected_attendees', 'absent_members', 'absent_count', 'special_guests',
            'agenda_items', 'has_agenda', 'all_motions', 'motions_count', 'standalone_motions',
            'all_action_items', 'decisions', 'general_notes', 'resolutions_noted',
            'has_transcript', 'transcript_text', 'transcript_file_url',
            'quorum_met', 'quorum_required', 'quorum_threshold', 'quorum_status',
            'officers_list', 'officers_count', 'board_member_count', 'members',
            'documents_filed', 'filing_location', 'task_title', 'task_category', 'task_due_date',
            'current_date', 'current_year', 'current_user', 'effective_date', 'appointment_date',
            'una_address', 'una_city', 'una_state', 'una_zip', 'una_ein'
          ];
          
          // Combine editable + preserved fields for filtering
          const allowedMeetingMinutesFields = [...editableFields, ...preservedPhase1Fields];
          
          // Filter merge_data to only allowed fields (keeps all Phase 1 data + 3 editable)
          fields = fields.filter((f: any) => allowedMeetingMinutesFields.includes(f.name));
          
          // Phase 2: NO editable fields in UI (view-only mode)
          uiEditableFields = [];
          
          console.log('✅ Meeting Minutes view-only mode active:');
          console.log('  - Total fields preserved for template:', fields.length);
          console.log('  - UI editable fields:', uiEditableFields.length, '(Phase 2 view-only)');
        } else if (isAddMemberWorkflow) {
          const beforeCount = fields.length;
          
          console.log('👤 ADD MEMBER: View-only mode (Phase 2)');
          console.log('  - All data entered in Phase 1');
          console.log('  - No merge fields editable in Phase 2');
          
          // All fields preserved for template but hidden from UI
          uiEditableFields = [];
          
          console.log('✅ Add Member view-only mode active:');
          console.log('  - Total fields preserved for template:', fields.length);
          console.log('  - UI editable fields:', uiEditableFields.length, '(Phase 2 view-only)');
        }
        
        if (!resolutionContext && !meetingMinutesContext && !isAddMemberWorkflow) {
          console.log('ℹ️ Standard document - no field filtering applied');
          
          // Only add optional_added_section for standard documents
          if (!fields.some((f: any) => f.name === 'optional_added_section')) {
            fields.push({
              name: 'optional_added_section',
              label: 'Optional Additional Content',
              type: 'textarea',
              description: 'Optional additional content (appears above ADOPTED section)',
              required: false,
            });
          }
        }

        // Reorder fields to match the requested display order
        const desiredOrder = resolutionContext 
          ? [
              // Resolution workflow: strict 6-field order
              'una_name',
              'resolution_title',
              'resolution_category',
              'current_date',
              'whereas_clauses',
              'resolved_clauses',
            ]
          : meetingMinutesContext
          ? [
              // Meeting Minutes workflow: 3 editable fields
              'una_name',
              'board_members_list',
              'signature_date',
            ]
          : [
              // Non-resolution workflows: default order
              'una_name',
              'una_constitution_date',
              'resolution_title',
              'resolution_category',
              'treasury_secretary_name',
              'board_members_list',
              'new_member_full_name',
              'new_member_address',
              'appointment_date',
              'effective_date',
              'signature_date',
              'optional_added_section',
            ];

        // Sort fields for resolution/meeting/add member context using uiEditableFields, otherwise use all fields
        // For Meeting Minutes and Add Member with no editable fields, use empty array
        const fieldsToSort = (meetingMinutesContext || isAddMemberWorkflow) && uiEditableFields.length === 0
          ? []
          : ((resolutionContext || meetingMinutesContext) && uiEditableFields.length > 0) 
            ? uiEditableFields 
            : fields;
        
        fieldsToSort.sort((a: any, b: any) => {
          const ia = desiredOrder.indexOf(a.name);
          const ib = desiredOrder.indexOf(b.name);
          if (ia === -1 && ib === -1) return 0;
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
        
        // Meeting Minutes: Force empty merge fields (view-only mode)
        if (meetingMinutesContext) {
          console.log('📋 Meeting Minutes: Setting mergeFields to [] (no panel)');
          setMergeFields([]);
        } else {
          setMergeFields(Array.isArray(fieldsToSort) ? fieldsToSort : []);
        }
        
        // Convert date fields to ISO format for input controls
        const convertedMergeData = { ...mergeData };
        fields.forEach((field: any) => {
          if (field.type === 'date' && convertedMergeData[field.name]) {
            // Convert "November 8, 2025" → "2025-11-08"
            convertedMergeData[field.name] = toISODate(convertedMergeData[field.name]);
          }
        });
        
        // Recompute board member fields with current data from database
        if (docData.una_id) {
          try {
            const { data: currentBoardMembers } = await supabase
              .from('una_members')
              .select('id, full_name, member_role, role, email')
              .eq('una_id', docData.una_id)
              .eq('status', 'active')
              .or('member_role.eq.board_member_active,member_role.eq.board_member_passive');
            
            if (currentBoardMembers && currentBoardMembers.length > 0) {
              // Fetch UNA data for officer detection
              const { data: unaData } = await supabase
                .from('unas')
                .select('treasury_secretary_id')
                .eq('id', docData.una_id)
                .single();

              // Helper: Strict officer detection
              const isOfficer = (member: any, una: any): boolean => {
                if (member.user_id === una?.treasury_secretary_id) return true;
                if (member.member_role === 'managing_member') return true;
                return false;
              };

              // Helper: Get display role - use member.role directly if available
              const getDisplayRole = (member: any, una: any): string => {
                // Priority 1: Use actual role field
                if (member?.role && member.role.trim() !== '') {
                  return member.role;
                }
                // Priority 2: Derive from status
                if (member.user_id === una?.treasury_secretary_id) {
                  return una?.primary_officer_role || 'Secretary Treasurer';
                }
                if (member.member_role === 'managing_member') {
                  return 'Managing Member';
                }
                return 'Board Member';
              };

              // Fetch all active members (including officers and board members)
              const { data: allMembers } = await supabase
                .from('una_members')
                .select('id, full_name, member_role, role, email, user_id')
                .eq('una_id', docData.una_id)
                .eq('status', 'active');

              // Separate officers and board members
              const officers = allMembers?.filter(m => isOfficer(m, unaData)) || [];
              const boardMembers = allMembers?.filter(m => 
                !isOfficer(m, unaData) && 
                (m.member_role === 'board_member_active' || m.member_role === 'board_member_passive')
              ) || [];

              // Update officer fields
              convertedMergeData.officers_count = officers.length;
              convertedMergeData.officers_list = officers.length > 0
                ? officers.map((o: any, idx: number) => 
                    `${idx + 1}. ${o.full_name || 'N/A'} - ${getDisplayRole(o, unaData)}`
                  ).join('\n')
                : 'No officers currently appointed';
              
              // Update board member fields (excluding officers)
              convertedMergeData.board_member_count = boardMembers.length;
              convertedMergeData.board_member_1_name = boardMembers[0]?.full_name || '';
              convertedMergeData.board_member_1_role = boardMembers[0] ? getDisplayRole(boardMembers[0], unaData) : '';
              convertedMergeData.board_member_2_name = boardMembers[1]?.full_name || '';
              convertedMergeData.board_member_2_role = boardMembers[1] ? getDisplayRole(boardMembers[1], unaData) : '';
              convertedMergeData.board_member_3_name = boardMembers[2]?.full_name || '';
              convertedMergeData.board_member_3_role = boardMembers[2] ? getDisplayRole(boardMembers[2], unaData) : '';
              convertedMergeData.board_member_4_name = boardMembers[3]?.full_name || '';
              convertedMergeData.board_member_4_role = boardMembers[3] ? getDisplayRole(boardMembers[3], unaData) : '';
              convertedMergeData.board_member_5_name = boardMembers[4]?.full_name || '';
              convertedMergeData.board_member_5_role = boardMembers[4] ? getDisplayRole(boardMembers[4], unaData) : '';
              convertedMergeData.board_members_list = boardMembers.length > 0
                ? boardMembers.map((bm: any, idx: number) => 
                    `${idx + 1}. ${bm.full_name || 'N/A'} - ${getDisplayRole(bm, unaData)}`
                  ).join('\n')
                : 'No board members currently appointed';
              
              console.log('✅ Recomputed officer and board member fields with current data');
            }
          } catch (error) {
            console.warn('Failed to recompute board member fields:', error);
          }
        }

        // Remove disallowed fields for resolution context (BUT NOT for add_member workflows!)
        if (resolutionContext && !isAddMemberWorkflow) {
          const disallowedInResolution = [
            'member_full_name', 'new_member_full_name', 'member_first_name', 'member_last_name',
            'member_role', 'new_member_role', 'member_address', 'new_member_address',
            'new_member_appointment_date', 'member_email', 'member_phone',
            'board_member_1_name', 'board_member_1_role', 'board_member_2_name', 'board_member_2_role',
            'board_member_3_name', 'board_member_3_role', 'board_member_4_name', 'board_member_4_role',
            'board_member_5_name', 'board_member_5_role',
            'documents_filed', 'filing_location', 'managing_members',
            'task_title', 'task_category', 'task_description', 'task_due_date',
            'members', // Keep board_members array for template signatures
          ];
          disallowedInResolution.forEach(k => {
            delete convertedMergeData[k];
          });
          console.log('🧹 Removed', disallowedInResolution.length, 'disallowed fields for resolution context');
        }
        
        setMergeValues(convertedMergeData);

        console.log('📊 Merge Field Summary:');
        console.log('  - Workflow:', workflowKey || 'unknown');
        console.log('  - Total fields available:', fields.length);
        console.log('  - Filtered from:', Object.keys(mergeData).length, 'original fields');
        console.log('  - Fields:', fields.map(f => f.name).sort().join(', '));

        // Load sections
        const { data: sectionsData, error: sectionsError } = await supabase
          .from('document_sections')
          .select('*')
          .eq('document_id', documentId)
          .order('section_number');

        if (sectionsError) {
          console.warn('No sections found or error loading sections:', sectionsError);
        }
        
        // Self-heal: If no sections found, try to create them
        if (!sectionsData || sectionsData.length === 0) {
          console.log('No sections found, attempting self-heal');
          await selfHealSections(docData, documentId);
        } else {
          setSections(sectionsData);
        }
      }
    } catch (error: any) {
      console.error('Error loading document:', error);
      toast({
        title: "Error",
        description: "Failed to load document",
        variant: "destructive",
      });
      navigate('/governance/tasks');
    } finally {
      setLoading(false);
    }
  };

  const editor = useEditor({
    extensions: [StarterKit],
    content: document?.is_custom ? (document.custom_content || '') : (sections[activeSection]?.section_content || ''),
    onUpdate: ({ editor }) => {
      if (document?.is_custom) {
        // For custom documents, update the document directly
        setDocument({ ...document, custom_content: editor.getHTML() });
      } else {
        // For template documents, update sections
        const updatedSections = [...sections];
        updatedSections[activeSection] = {
          ...updatedSections[activeSection],
          section_content: editor.getHTML(),
        };
        setSections(updatedSections);
      }
    },
  });

  // Update editor when active section changes or document loads
  useEffect(() => {
    if (editor && document) {
      if (document.is_custom) {
        editor.commands.setContent(document.custom_content || '');
      } else if (sections[activeSection]) {
        editor.commands.setContent(sections[activeSection].section_content || '');
      }
    }
  }, [activeSection, editor, document]);

  // Update preview when document or merge values change
  useEffect(() => {
    if (document && !document.is_custom) {
      updatePreview();
    }
  }, [document, mergeValues, updatePreview]);

  const handleSaveCustomDocument = async () => {
    if (!document) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('generated_documents')
        .update({
          custom_content: document.custom_content,
        })
        .eq('id', documentId);

      if (error) throw error;

      toast({
        title: "Document Saved",
        description: "Your changes have been saved",
      });
    } catch (error: any) {
      console.error('Error saving custom document:', error);
      toast({
        title: "Error",
        description: "Failed to save document",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSection = async () => {
    if (document?.is_custom) {
      // Save custom document
      return handleSaveCustomDocument();
    }

    if (!sections[activeSection]) return;

    setSaving(true);
    try {
      const section = sections[activeSection];
      const { error } = await supabase
        .from('document_sections')
        .update({
          section_content: section.section_content,
        })
        .eq('id', section.id);

      if (error) throw error;

      toast({
        title: "Section Saved",
        description: "Next: Fill in merge fields, then preview your document",
      });
    } catch (error: any) {
      console.error('Error saving section:', error);
      toast({
        title: "Error",
        description: "Failed to save section",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMergeFields = async () => {
    // Check if custom edits exist
    if (hasCustomEdits) {
      const confirmed = window.confirm(
        "⚠️ You've made custom edits to this document. " +
        "Updating merge fields will OVERWRITE your edits. " +
        "Are you sure you want to continue?"
      );
      
      if (!confirmed) return;
      
      // Clear custom edits if user confirms
      await supabase
        .from('generated_documents')
        .update({
          custom_rendered_content: null,
          has_custom_edits: false,
        })
        .eq('id', documentId);
      
      setHasCustomEdits(false);
    }

    setSaving(true);
    try {
      // Convert date fields back to readable format for storage/display
      const convertedValues = { ...mergeValues };
      mergeFields.forEach((field: any) => {
        if (field.type === 'date' && convertedValues[field.name]) {
          // Convert "2025-11-08" → "November 8, 2025"
          convertedValues[field.name] = toReadableDate(convertedValues[field.name]);
        }
      });
      
      const { error } = await supabase
        .from('generated_documents')
        .update({
          merge_data: convertedValues,
          has_custom_edits: false,
        })
        .eq('id', documentId);

      if (error) throw error;

      // Update preview after saving fields
      await updatePreview();

      toast({
        title: "Fields Saved",
        description: "Preview updated with your changes",
      });
    } catch (error: any) {
      console.error('Error saving merge fields:', error);
      toast({
        title: "Error",
        description: "Failed to save merge fields",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getHtmlForPdf = async (overrideMergeData?: Record<string, any>): Promise<string> => {
    // Priority 1: Use custom edits if they exist
    if (document?.custom_rendered_content) {
      return document.custom_rendered_content;
    }

    // Priority 2: Custom document content
    if (document?.is_custom) {
      const bodyContent = extractBodyContent(document.custom_content || '');
      return bodyContent || '<p>Empty</p>';
    }

    // Priority 3: Render template with merge data
    const templateId = document?.template_library_id || document?.template_id;
    const templateTable = document?.template_library_id 
      ? 'document_template_library' 
      : 'document_templates';
    
    const { data: template } = await supabase
      .from(templateTable)
      .select('template_content')
      .eq('id', templateId)
      .order('version', { ascending: false })
      .limit(1)
      .single();
    
    if (template?.template_content) {
      const Handlebars = (await import('handlebars')).default;
      
      // DIAGNOSTIC: Log the raw template
      console.log('🔵 [DIAGNOSTIC] Raw template_content (first 500 chars):');
      console.log(template.template_content.substring(0, 500));
      console.log('🔵 [DIAGNOSTIC] Raw template_content (LAST 500 chars):');
      console.log(template.template_content.substring(Math.max(0, template.template_content.length - 500)));
      console.log('🔵 [DIAGNOSTIC] Does template contain "signature-block"?', 
        template.template_content.includes('signature-block'));
      console.log('🔵 [DIAGNOSTIC] Does template contain "signature-line"?',
        template.template_content.includes('signature-line'));
      console.log('🔵 [DIAGNOSTIC] Template total length:', template.template_content.length);
      
      // IMPORTANT: Normalize data before compiling template
      const preparedMergeData: Record<string, any> = { ...(overrideMergeData || mergeValues || {}) };
      
      // Convert whereas_clauses to array if it's a string
      if (typeof preparedMergeData.whereas_clauses === 'string') {
        preparedMergeData.whereas_clauses = preparedMergeData.whereas_clauses
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0);
        console.log('✅ Converted whereas_clauses string to array for PDF');
      }
      
      // Convert resolved_clauses to array if it's a string
      if (typeof preparedMergeData.resolved_clauses === 'string') {
        preparedMergeData.resolved_clauses = preparedMergeData.resolved_clauses
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0);
        console.log('✅ Converted resolved_clauses string to array for PDF');
      }
      
      // Ensure board_members is an array
      if (!Array.isArray(preparedMergeData.board_members)) {
        preparedMergeData.board_members = [];
      }
      
      // Default requires_board_approval if missing
      if (typeof preparedMergeData.requires_board_approval === 'undefined') {
        preparedMergeData.requires_board_approval = preparedMergeData.board_members.length > 0;
      }
      
      // Compile with Handlebars
      const compiled = Handlebars.compile(template.template_content);
      const html = compiled(preparedMergeData);
      
      // DIAGNOSTIC: Log the compiled HTML
      console.log('🔵 [DIAGNOSTIC] Compiled HTML (first 500 chars):');
      console.log(html.substring(0, 500));
      console.log('🔵 [DIAGNOSTIC] Compiled HTML (LAST 500 chars):');
      console.log(html.substring(Math.max(0, html.length - 500)));
      console.log('🔵 [DIAGNOSTIC] Does compiled HTML contain "signature-block"?',
        html.includes('signature-block'));
      console.log('🔵 [DIAGNOSTIC] Does compiled HTML contain "signature-line"?',
        html.includes('signature-line'));
      console.log('🔵 [DIAGNOSTIC] Compiled HTML total length:', html.length);
      
      // Return complete HTML document for server-side PDF generation
      // PDFShift requires full DOCTYPE + <html> structure
      console.log('🔍 [HTML] First 200 chars:', html.substring(0, 200));
      console.log('🔍 [HTML] Starts with DOCTYPE?', html.trim().startsWith('<!DOCTYPE'));
      return html;
    }

    // Priority 4: Fallback to sections
    return sections
      .map(s => `<h2>${s.section_title || ''}</h2>${s.section_content || ''}`)
      .join('<hr/>');
  };

  const handleGeneratePdfPreview = async () => {
    setGeneratingPreview(true);
    try {
      const html = await getHtmlForPdf();
      
      // Generate PDF preview (blob URL)
      const blobUrl = await generatePdfPreview(html);
      
      // Clean up old blob URL if exists
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      
      setPdfPreviewUrl(blobUrl);
      
      // Open in new tab (avoids iframe blocking issues)
      window.open(blobUrl, '_blank');
      
      toast({
        title: "PDF Preview Generated",
        description: "Opening in new tab. You can also download it.",
      });
    } catch (error: any) {
      console.error('Error generating PDF preview:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate PDF preview",
        variant: "destructive",
      });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleFinalize = async () => {
    // Get workflow and template context with multiple fallback checks
    const templateKey = document?.template_lib?.template_key || '';
    const workflowKey = document?.workflow?.workflow_key || '';
    const documentName = document?.document_name || '';
    
    const isMeetingMinutesTemplate = templateKey === 'formal_meeting_minutes';
    const isMeetingMinutesByName = documentName.toLowerCase().includes('meeting minutes');
    
    const meetingMinutesContext = 
      (workflowKey === 'recording_minutes') || 
      isMeetingMinutesTemplate ||
      isMeetingMinutesByName;
    
    console.log('🔍 Finalize Detection:', {
      templateKey,
      workflowKey,
      documentName,
      isMeetingMinutesTemplate,
      isMeetingMinutesByName,
      meetingMinutesContext
    });
    
    // Meeting Minutes: Show signature modal
    if (meetingMinutesContext) {
      console.log('📋 Meeting Minutes detected - opening signature modal');
      setShowFinalizeModal(true);
      return;
    }
    
    // Other documents: Standard finalize flow
    // Check required fields for template-based documents
    if (!document?.is_custom) {
      const requiredFields = mergeFields.filter((f: any) => f.required);
      const missingFields = requiredFields.filter((f: any) => !mergeValues[f.name]);

      if (missingFields.length > 0) {
        toast({
          title: "Missing Required Fields",
          description: `Please fill in: ${missingFields.map((f: any) => f.name).join(', ')}`,
          variant: "destructive",
        });
        return;
      }
    }

    if (!confirm('Finalize this document? This will generate and save a PDF.')) {
      return;
    }

    setSaving(true);
    
    // Show loading toast with progress feedback
    toast({
      title: "Generating PDF...",
      description: "This may take a few moments for long documents.",
    });
    
    try {
      const { file_url, file_path, file_size } = await generateDocumentPdf(documentId as string, document.document_name);

      const { error } = await supabase
        .from('generated_documents')
        .update({
          status: 'final',
          finalized_at: new Date().toISOString(),
          finalized_by: user?.id,
          file_url,
          file_path,
          file_size,
        })
        .eq('id', documentId);

      if (error) throw error;

      toast({
        title: "Document Finalized",
        description: "PDF generated and saved successfully",
      });

      // Smart navigation based on document type
      if (isWorkflowDocument) {
        navigate(`/workflows?tab=my-workflows`);
      } else {
        navigate(`/governance/tasks`);
      }
    } catch (error: any) {
      console.error('Error finalizing document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate and save PDF",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleFinalizeWithSignatures = async (signatures: {
    recording_secretary_signature: string;
    recording_secretary_signature_date: string;
    president_signature: string;
    president_signature_date: string;
  }) => {
    try {
      console.log('🔵 [FINALIZE] Starting finalization with Puppeteer PDF generation...');
      setSaving(true);
      
      // Show loading toast
      const loadingToast = toast({
        title: "Generating professional PDF with signatures...",
        description: "This may take 15-30 seconds. Using Chromium print engine for perfect page breaks.",
      });
      
      // 1. Fetch client IP for audit trail
      const clientIP = await getClientIP();
      
      // 2. Create updated merge data with signatures
      const updatedMergeData = {
        ...mergeValues,
        ...signatures,
        signature_ip: clientIP,
        signed_by_user_id: user?.id,
      };
      
      console.log('🔵 [FINALIZE] Merge data prepared with signatures');
      
      // 3. Generate HTML with embedded signatures
      const html = await getHtmlForPdf(updatedMergeData);
      
      console.log('🔵 [FINALIZE] HTML generated, calling Puppeteer edge function...');
      
      // 4. Get auth session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session - please log in again');
      }
      
      // 5. Call Puppeteer edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF');
      }
      
      const { pdf: pdfBase64, size } = await response.json();
      
      console.log('✅ [FINALIZE] PDF generated by Puppeteer:', size, 'bytes');
      
      // 6. Convert base64 to Blob
      const pdfBlob = base64ToBlob(pdfBase64, 'application/pdf');
      
      // 7. Upload to storage (using existing 'documents' bucket)
      const filePath = generatedFilePath(null, `${document?.document_name || "document"}_signed`, documentId);
      
      console.log('🔵 [FINALIZE] Uploading PDF to storage:', filePath);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });
      
      if (uploadError) {
        console.error('❌ [FINALIZE] Storage upload error:', uploadError);
        throw uploadError;
      }
      
      console.log('✅ [FINALIZE] PDF uploaded successfully');
      
      // 8. Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);
      
      const file_url = publicUrlData.publicUrl;
      
      // 9. Update document record
      const { error: updateError } = await supabase
        .from('generated_documents')
        .update({
          merge_data: updatedMergeData,
          status: 'final',
          finalized_at: new Date().toISOString(),
          finalized_by: user?.id,
          file_url,
          file_path: filePath,
          file_size: size,
          all_signatures_collected: true,
          signed_file_url: file_url,
        })
        .eq('id', documentId);
      
      if (updateError) {
        console.error('❌ [FINALIZE] Database update error:', updateError);
        throw updateError;
      }
      
      console.log('✅ [FINALIZE] Document finalized successfully');
      
      // 10. Success feedback
      toast({
        title: "Document Finalized!",
        description: "Professional PDF generated with perfect page breaks.",
      });
      
      // 11. Close modal and navigate
      setShowFinalizeModal(false);
      
      if (isWorkflowDocument) {
        navigate(`/workflows?tab=my-workflows`);
      } else {
        navigate(`/governance/tasks`);
      }
      
    } catch (error: any) {
      console.error('❌ [FINALIZE] Error:', error);
      toast({
        title: "Failed to finalize",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!document) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4">Document Not Found</h2>
          <Button onClick={() => navigate('/governance/tasks')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tasks
          </Button>
        </div>
      </MainLayout>
    );
  }

  const requiredMergeFields = mergeFields.filter((f: any) => f.required);
  const missingRequiredFields = requiredMergeFields.filter((f: any) => !mergeValues[f.name]);
  
  // ✅ Better display of missing fields
  const missingFieldLabels = missingRequiredFields.map((f: any) => f.label || f.name || 'unnamed field');

  return (
    <MainLayout>
      <div className="max-w-full mx-0 px-2">
      {/* Header Section */}
      <div className="border-b bg-card px-2 py-4 mb-6 sticky top-0 z-10 bg-background">
          <div className="flex items-center justify-between gap-4">
            {/* Back Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="shrink-0"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              {/* Document Title */}
              <div className="flex-1 min-w-0 px-6">
                <h1 className="text-xl font-semibold tracking-tight truncate">
                  {document.document_name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge 
                    variant={
                      document.status === 'finalized' ? 'default' : 
                      document.status === 'draft' ? 'secondary' : 
                      'outline'
                    }
                    className="text-xs"
                  >
                    {document.status.replace('_', ' ')}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    v{document.version || 1}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0">
                {/* Save button - only show when merge fields exist */}
                {mergeFields.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveMergeFields}
                    disabled={saving || document.status === 'finalized'}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGeneratePdfPreview}
                  disabled={generatingPreview}
                >
                  {generatingPreview ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview PDF
                    </>
                  )}
                </Button>
                
                 <Button
                   size="sm"
                   onClick={handleFinalize}
                   disabled={document.status === 'finalized' || saving || missingRequiredFields.length > 0}
                 >
                   <FileCheck className="h-4 w-4 mr-2" />
                   Finalize
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          {/* Warning for missing required fields */}
          {missingRequiredFields.length > 0 && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Missing Required Fields:</strong> {missingFieldLabels.join(', ')}
              </AlertDescription>
            </Alert>
          )}

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Panel - Merge Fields or Instructions (4 columns) */}
            {!document.is_custom && mergeFields && mergeFields.length > 0 && !addMemberContext && (
              <div className="lg:col-span-4">
                <div className="sticky top-24 space-y-4">
                {/* Merge Fields Card */}
                <Card>
                  <CardHeader>
                    <h3 className="font-semibold text-base">Merge Fields</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Edit document fields below. Changes update in real-time.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                      {mergeFields.map((field: any, idx: number) => {
                        const type = field.type || 'text';
                        const isArrayField = field.name === 'whereas_clauses' || field.name === 'resolved_clauses';
                        
                        // For array fields, display as newline-separated text
                        const displayValue = isArrayField && Array.isArray(mergeValues[field.name])
                          ? mergeValues[field.name].join('\n')
                          : mergeValues[field.name] || '';
                        
                        const displayLabel = field.label || field.name || 'Field';
                        const isOptionalSection = field.name === 'optional_added_section';
                        
                        return (
                          <div key={idx}>
                            <Label className="text-sm font-medium">
                              {displayLabel}
                              {field.required && <span className="text-destructive ml-1">*</span>}
                              {isOptionalSection && (
                                <span className="text-muted-foreground text-xs ml-2">(no heading in document)</span>
                              )}
                            </Label>
                            {isArrayField ? (
                              <>
                                <Textarea
                                  value={displayValue}
                                  onChange={(e) => handleMergeFieldChange(field.name, e.target.value)}
                                  placeholder={`Enter ${displayLabel} (one per line)`}
                                  className="mt-2"
                                  rows={5}
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Enter one clause per line
                                </p>
                              </>
                            ) : type === 'textarea' ? (
                              <Textarea
                                value={displayValue}
                                onChange={(e) => handleMergeFieldChange(field.name, e.target.value)}
                                placeholder={field.description || field.name}
                                className="mt-2"
                                rows={4}
                              />
                            ) : (
                              <Input
                                type={type === 'date' ? 'date' : 'text'}
                                value={displayValue}
                                onChange={(e) => handleMergeFieldChange(field.name, e.target.value)}
                                placeholder={field.description || field.name}
                                className="mt-2"
                              />
                            )}
                            {isOptionalSection && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Content appears in a styled box without a heading
                              </p>
                            )}
                          </div>
                        );
                      })}
                      {mergeFields.length > 0 && (
                        <Button onClick={handleSaveMergeFields} size="sm" className="w-full mt-4" disabled={saving}>
                          <Save className="h-4 w-4 mr-2" />
                          Save Fields
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Sections Navigation */}
                {sections.length > 0 && (
                  <Card>
                    <CardHeader>
                      <h3 className="font-semibold text-base">Document Sections</h3>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {sections.map((section, idx) => (
                          <Button
                            key={section.id}
                            variant={activeSection === idx ? "secondary" : "ghost"}
                            className="w-full justify-start text-left"
                            onClick={() => setActiveSection(idx)}
                            disabled={section.is_locked}
                          >
                            <span className="truncate">
                              {idx + 1}. {section.section_title}
                            </span>
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

            {/* Right Panel - Document Preview (8 columns when merge fields, 12 when not) */}
            <div className={document.is_custom || !mergeFields || mergeFields.length === 0 ? "lg:col-span-12" : "lg:col-span-8"}>
              <Card className="shadow-xl border-2">
                <CardContent className="p-0">
                  {!document.is_custom && (
                    <>
                      <div className="px-6 py-4 border-b bg-muted/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Eye className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Live preview • Updates automatically as you edit
                            </span>
                          </div>
                          {hasCustomEdits && (
                            <Badge variant="secondary" className="text-xs">
                              Custom Edits Applied
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Document Preview with Paper Styling */}
                      <div className="bg-gray-100 p-2 min-h-[calc(100vh-200px)]">
                        <div 
                          className="bg-white shadow-xl mx-auto rounded-sm max-w-none w-full"
                          style={{
                            minHeight: '11in',
                            padding: '0.75in',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div
                            className="prose prose-sm max-w-none"
                            style={{
                              fontFamily: "Cambria, Georgia, 'Times New Roman', serif",
                              fontSize: '12pt',
                              lineHeight: '1.6',
                            }}
                          >
                            <style>
                              {`
                                /* Override prose spacing to match PDF exactly */
                                .prose h1 { 
                                  font-size: 16pt; 
                                  font-weight: bold; 
                                  margin: 0 0 12pt 0;
                                  text-align: center;
                                }
                                .prose h2 { 
                                  font-size: 13pt; 
                                  font-weight: bold; 
                                  margin: 16pt 0 8pt 0;
                                  border-bottom: 1px solid #333;
                                  padding-bottom: 4pt;
                                }
                                .prose h3 { 
                                  font-size: 12pt; 
                                  font-weight: bold; 
                                  margin: 12pt 0 6pt 0;
                                }
                                .prose p { 
                                  margin: 0 0 8pt 0;
                                  font-size: 12pt;
                                }
                                .prose li { 
                                  margin: 4pt 0;
                                  font-size: 12pt;
                                }
                                .prose ul, .prose ol {
                                  margin: 8pt 0;
                                  padding-left: 24pt;
                                }
                                .prose strong, .prose b { font-weight: bold; }
                                .prose em, .prose i { font-style: italic; }
                                
                                /* Signature block styles to match PDF */
                                .prose .signature-block {
                                  margin-top: 48pt;
                                  margin-bottom: 24pt;
                                }
                                
                                .prose .signature-line {
                                  border-bottom: 1px solid #000;
                                  width: 300pt;
                                  margin-bottom: 4pt;
                                  height: 1px;
                                }
                              `}
                            </style>
                            <div dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(previewHtml || '<p class="text-muted-foreground">Loading preview...</p>') }} />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

              {document.is_custom && (
                /* Custom Document Editor (unchanged) */
                <>
                  <div className="mb-4 px-6 pt-6">
                    <h2 className="text-xl font-bold">Document Content</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Edit your custom document below. Merge fields have been pre-populated with your data.
                    </p>
                  </div>

                  <Separator className="mb-4" />

                  <div className="prose max-w-none px-6 pb-6">
                    <div className="border rounded-lg p-4 min-h-[500px] focus-within:ring-2 focus-within:ring-ring">
                      <EditorContent editor={editor} />
                    </div>
                    
                    {/* Editor Toolbar */}
                    <div className="flex gap-2 mt-4 p-2 border rounded-lg bg-muted/50">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleBold().run()}
                        className={editor?.isActive('bold') ? 'bg-accent' : ''}
                      >
                        Bold
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleItalic().run()}
                        className={editor?.isActive('italic') ? 'bg-accent' : ''}
                      >
                        Italic
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={editor?.isActive('heading', { level: 1 }) ? 'bg-accent' : ''}
                      >
                        H1
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={editor?.isActive('heading', { level: 2 }) ? 'bg-accent' : ''}
                      >
                        H2
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleBulletList().run()}
                        className={editor?.isActive('bulletList') ? 'bg-accent' : ''}
                      >
                        Bullet List
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                        className={editor?.isActive('orderedList') ? 'bg-accent' : ''}
                      >
                        Numbered List
                      </Button>
                    </div>
                  </div>
                </>
                )}
                  </CardContent>
                </Card>
            </div>
          </div>
          
          {/* Signature Modal for Meeting Minutes */}
          {showFinalizeModal && (
            <FinalizeWithSignatures
              recordingSecretaryName={mergeValues.recording_secretary_name || 'Unknown'}
              presidentName={mergeValues.president_name || 'Unknown'}
              onFinalize={handleFinalizeWithSignatures}
              onCancel={() => setShowFinalizeModal(false)}
            />
          )}
      </div>
    </MainLayout>
  );
}
